import * as React from 'react';
import {
  FileBarChart,
  LayoutDashboard,
  LogOut,
  Megaphone,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router';

import { NavMain } from './nav-main';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '../ui/sidebar';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { routes } from '@/routing';
import { useTenantStaffAuth } from '@/hooks/use-tenant-staff-auth';
import { useTenantTheme } from '@/hooks/use-tenant-theme';
import { useTranslation } from 'react-i18next';

export const TenantStaffAppSidebar: React.FC = ({
  ...props
}: React.ComponentProps<typeof Sidebar>) => {
  const navigate = useNavigate();
  const { logout } = useTenantStaffAuth();
  const { tenant } = useTenantTheme();
  const { t } = useTranslation();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = React.useState(false);
  const tenantName = tenant?.name ?? 'Tenant Staff';
  const tenantLogo = tenant?.logoUrl ?? null;

  const data = React.useMemo(
    () => ({
      navMain: [
        {
          title: 'Dashboard',
          url: routes.tenantStaffDashboard,
          icon: LayoutDashboard,
          isActive: true,
        },
        {
          title: 'Campaigns',
          url: routes.tenantStaffCampaigns,
          icon: Megaphone,
        },
        {
          title: 'Advertisers',
          url: routes.tenantStaffAdvertisers,
          icon: Wallet,
        },
        {
          title: 'Users',
          url: routes.tenantStaffUsers,
          icon: Users,
        },
        {
          title: 'Admins',
          url: routes.tenantStaffStaff,
          icon: UserCog,
        },
        {
          title: 'Reports',
          url: routes.tenantStaffConsolidatedReport,
          icon: FileBarChart,
        },
      ],
    }),
    []
  );

  const handleConfirmLogout = React.useCallback(() => {
    logout();
    setIsLogoutDialogOpen(false);
    navigate(routes.tenantStaffLogin, { replace: true });
  }, [logout, navigate]);

  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      className="w-64 p-0 border-r bg-secondary border-slate-200"
      {...props}
    >
      <SidebarHeader className="p-4 border-b border-secondary bg-gradient-main">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="hover:bg-transparent group-data-[collapsible=icon]:justify-center"
              tooltip={tenantName}
            >
              <Link
                to={routes.tenantStaffDashboard}
                className="flex items-center gap-3 focus-visible:bg-transparent"
              >
                <img
                  src={tenantLogo ?? '/images/png/pathiq-logo.png'}
                  alt={tenantName}
                  className="shrink-0 group-data-[collapsible=icon]:hidden h-12"
                />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="flex-1 py-4 pl-2 overflow-y-auto">
        <NavMain items={data.navMain} />
      </SidebarContent>
      <SidebarFooter className="p-2 border-t-[0.4px]">
        <SidebarMenu className="pl-2">
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setIsLogoutDialogOpen(true)}
              className="font-semibold transition-all duration-200 rounded-md px-3 py-4 text-base group-data-[collapsible=icon]:justify-center text-white hover:bg-gradient-main hover:text-white"
            >
              <LogOut className="shrink-0" />
              <span className="group-data-[collapsible=icon]:hidden">
                {t('navigation.logout')}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <ConfirmDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        title={t('tenantStaff.logoutConfirmTitle')}
        description={t('tenantStaff.logoutConfirmDescription')}
        confirmLabel={t('navigation.logout')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmLogout}
      />
      <SidebarRail />
    </Sidebar>
  );
};
